import { z } from "zod";
import {
  normalizeApiBaseUrl,
  parseErrorResponse,
  SharedApiError,
  type FetchLike,
} from "./shared";

/**
 * POST /api/users/bootstrap — regional, client-driven user bootstrap.
 *
 * Option F.1 (multi-region cleanup): single regional API call that
 * replaces the legacy CF-Worker chain
 *
 *   client → POST /cp/users/init → regional POST /internal/users/bootstrap-regional
 *
 * with one direct regional write. The Worker chain (and the
 * `/internal/users/bootstrap-regional` endpoint it forwards to) stay
 * alive during F.1; this endpoint is purely additive. F.2 migrates the
 * WorkOS webhook off the Worker; F.3 switches the desktop client to
 * call this endpoint; F.4-F.7 mothball the Worker.
 *
 * See `.claude/plans/look-at-this-issue-imperative-nygaard.md`
 * Phase 1 / Option F.
 *
 * Auth model: standard WorkOS JWT in `Authorization: Bearer ...`. The
 * server enforces `body.user_id === JWT.sub` so a caller can only
 * bootstrap their own user row.
 *
 * Side effects (in one regional Postgres transaction):
 *   1. UPSERT `users` row (idempotent on retry).
 *   2. INSERT `user_presence` row with `ON CONFLICT (user_id) DO NOTHING`
 *      — preserves the invariant "every users row has a paired
 *      user_presence row" that PR #1762 patched into the legacy chain.
 *
 * Idempotent — safe for retries / multiple sign-in tabs.
 */

export const userBootstrapRequestSchema = z.object({
  user_id: z.string().min(1),
  // Email is required end-to-end. Same hardening rationale as the
  // legacy `/internal/users/bootstrap-regional` endpoint (PR #1654):
  // accepting empty email coerces to `''` which collides with
  // `users_email_unique` on the second empty-email signup.
  email: z.string().email().max(254),
  // WorkOS firstName + lastName for Google signups. When omitted the
  // server falls back to the email-local-part — preserves back-compat
  // with older clients that don't know about this field. Cap matches
  // the `users.display_name` column.
  display_name: z.string().min(1).max(100).nullable().optional(),
  // IANA timezone. Optional — clients that can't resolve it (e.g. early
  // iOS background-launch paths) omit the field and the server stores
  // NULL. Cap matches the regional handler's existing validation.
  timezone: z.string().min(1).max(64).nullable().optional(),
  // Client-derived from the device's IANA timezone at signup.
  // Persisted on the regional `users` row for downstream replica
  // routing. Stays a body field (not header / inferred) so the
  // client is the source of truth at first INSERT.
  //
  // **Write-once**: consumed only on the first INSERT for a given
  // `user_id`. On subsequent bootstraps (iOS calls this on every
  // sign-in), the server's `ON CONFLICT (id) DO UPDATE` clause
  // explicitly OMITS `home_region` — see
  // `server/routes/user-bootstrap-write-routes.ts`. The body's value
  // is silently ignored after the first INSERT. Don't reintroduce
  // it into the UPSERT branch without reading the commit log: PR
  // #1809 removed it because iOS travel (EU → US → EU) was flipping
  // the server-side region on each leg, which then cascaded into an
  // infinite desktop `<ZeroProvider>` remount loop via the (now-
  // deleted) `HomeRegionWatcher`.
  home_region: z.enum(["us", "eu"]),
});

export type UserBootstrapRequest = z.infer<typeof userBootstrapRequestSchema>;

export const userBootstrapWriteResponseSchema = z.object({
  ok: z.literal(true),
});

export type UserBootstrapWriteResponse = z.infer<
  typeof userBootstrapWriteResponseSchema
>;

export function userBootstrapWritePath(): string {
  return "/api/users/bootstrap";
}

export interface PostUserBootstrapInput {
  apiBaseUrl: string;
  accessToken: string;
  body: UserBootstrapRequest;
  fetchImpl?: FetchLike;
}

export async function postUserBootstrapRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: PostUserBootstrapInput): Promise<UserBootstrapWriteResponse> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${userBootstrapWritePath()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to bootstrap user");
  }

  const payload = await response.json().catch(() => null);
  const parsed = userBootstrapWriteResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid user-bootstrap-write response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
